import type { FontModel } from 'interfaces/app';
import { fontUtils } from 'utils';

export interface FitBoxOptions {
	kind: 'box';
	allowGrow?: boolean;
	allowShrink?: boolean;
}

export interface FitFontOptions {
	kind: 'font';
	boxHeight: number;
	boxWidth: number;
	minFontSize?: number;
	maxFontSize?: number;
}

export type FitOptions = (
	{
		fontFamily: string;
		fontModel?: FontModel;
		fontSize: number;
		isBold: boolean;
		maxWidth: number;
		text: string;
	}
	& (
		FitBoxOptions
		| FitFontOptions
	)
);

export interface FitResult {
	height: number;
	/**
	 * Indicates if the text overflows the container
	 */
	overflow?: boolean;
	usedFontSize: number;
	width: number;
}

interface MeasureResult {
	height: number;
	lines: string[];
	width: number;
}

interface MeasureWrappingOptions {
	fontFamily: string;
	fontModel?: FontModel;
	isBold: boolean;
	measureFn: (text: string) => number;
	size: number;
	text: string;
	widthLimit: number;
}

export interface WrapLinesResult {
	lines: string[];
	maxLineWidth: number;
}

export type WidthScaleKey = `${string}|${number}`;

export const SOFT_HYPHEN = '\u00AD';

let measurer: HTMLDivElement | null = null;
/**
 * LRU (Least Recently Used) capacity for per-font text width cache.
 */
const TEXT_WIDTH_CACHE_LIMIT = 1000;
/**
 * Per-font cache of recent measured strings.
 * Key:   `${fontFamily}|${fontSize}`
 * Value: Map<string, number>  (insertion order = recency)
 */
const textWidthCache = new Map<WidthScaleKey, Map<string, number>>();

/**
 * Split a single **over-wide** word into the smallest set of fragments whose
 * rendered width never exceeds `maxWidth`.
 *
 * The routine walks the word one *grapheme* at a time (so emoji, flags, or
 * ZWJ sequences stay intact) and uses a **one-glyph look-ahead**:
 *
 * ```
 * tentative = currentBuffer + currentGlyph + nextGlyph
 * if tentativeWidth > maxWidth -> break before nextGlyph
 * ```
 *
 * This guarantees that later, when a visible hyphen "-" is appended to every
 * fragment – exactly as the browser does when it wraps a soft-hyphen - the
 * line still fits inside `maxWidth`.
 * No cumulative “+1, +2, +3...” drift shows up, even after dozens of breaks.
 *
 * @param word        The word to break (must not contain whitespace).
 * @param canvasContext  A 2-D canvas context already configured with the
 *                       *exact* font used in the textarea/SVG; used for
 *                       precise `measureText()` calls.
 * @param maxWidth    Maximum allowed line width in CSS pixels.
 *
 * @returns An array of fragments.  Re-joining them with visible hyphens
 *          reproduces the original word, and each `fragment + '-'` is
 *          guaranteed <= `maxWidth`.
 */
function breakLongWord(
	word: string,
	maxWidth: number,
	measureFn: (text: string) => number,
): string[] {
	const parts: string[] = [];
	let buffer = '';
	/**
	 * Step 1 — convert the word into an array of *user-perceived* glyphs
	 *          (a.k.a. grapheme clusters).
	 *
	 * * Intl.Segmenter -> perfect: handles emoji ZWJ, regional-indicator flags,
	 *   Hangul Jamos, etc.
	 * * Fallback -> `Array.from()` which is good for BMP (+ surrogate pairs)
	 *   and cheap on engines that lack Segmenter.
	 */
	const glyphs: string[] = (
		(
			typeof Intl !== 'undefined'
			&& 'Segmenter' in Intl
		)
			? [
				...new (Intl as any).Segmenter(
					'und',
					{
						granularity: 'grapheme',
					},
				).segment(word),
			].map((seg) => seg.segment)
			: Array.from(word)
	);

	for (let characterIndex = 0; characterIndex < glyphs.length; characterIndex += 1) {
		const character = glyphs[characterIndex];
		const nextCharacter = (
			glyphs[characterIndex + 1]
			?? ''
		);
		const next = buffer + character;

		/**
		 * Measure: *current buffer* + *next glyph*.
		 * If that pair already overflows, `buffer` becomes a committed
		 * fragment and `glyph` starts the next one.
		 */
		if (
			measureFn(next + nextCharacter) > maxWidth
			&& buffer !== ''
		) {
			parts.push(buffer);
			buffer = character;
		} else {
			buffer = next;
		}
	}

	if (buffer) {
		parts.push(buffer);
	}

	return parts;
}

/**
 * measureWrapping() - wrap `text` at the given `size` and `widthLimit`,
 * returning line-count, widest line, and block height.
 *
 * Wrapping is re-computed each time because different font sizes
 * produce different break points.
 */
function measureWrapping(options: MeasureWrappingOptions): MeasureResult {
	const {
		fontFamily,
		fontModel,
		measureFn,
		size,
		text,
		widthLimit,
	} = options;

	const {
		lines,
		maxLineWidth,

	} = wrapLines(
		text,
		widthLimit,
		measureFn,
	);
	const height = lines.length * size * fontUtils.getEffectiveLineHeight({
		fontFamily,
		fontModel,
		fontSize: size,
		isBold: options.isBold,
	});

	return {
		height,
		lines,
		width: maxLineWidth,
	};
}

export function filter(
	text: string,
	textEncodingSupport: string[],
): string {
	if (
		typeof text === 'undefined'
		|| !text
	) {
		return '';
	}

	// Replace left- and right quotation marks to straight quotation marks
	text = text.replace(
		'’',
		'\'',
	);
	text = text.replace(
		'‘',
		'\'',
	);
	text = text.replace(
		'“',
		'"',
	);
	text = text.replace(
		'”',
		'"',
	);

	// Replace line break
	text = text.replace(
		'\u2028',
		'\n',
	);

	/**
	 * Remove soft-hyphens manually added
	 */
	text = text.replace(
		new RegExp(
			SOFT_HYPHEN,
			'g',
		),
		'',
	);

	/*
	 * Start filter string
	 * Check unicode ranges at http://jrgraphix.net/research/unicode_blocks.php
	 */
	let regexpString = '[^\u000A-\u000D,^\u0085,^\u2028-\u2029,^\\n,';

	if (textEncodingSupport.indexOf('latin') !== -1) {
		/*
		 * Basic latin:			0020 — 007F
		 * Latin-1 Supplement:	00A0 — 00FF
		 * Latin Extended-A:	0100 — 017F
		 * Latin Extended-B:	0180 — 024F
		 */
		regexpString += '^\u0020-\u0250,';
	}

	if (textEncodingSupport.indexOf('cyrillic') !== -1) {
		regexpString += '^\u0400-\u0500,';
	}

	// Remove last comma
	regexpString = regexpString.substring(
		0,
		regexpString.length - 1,
	);

	// Close filter string
	regexpString += ']';

	// Build regular expression
	const filterRegExp = new RegExp(
		regexpString,
		'g',
	);

	// Return filtered text
	return String(text).replace(
		filterRegExp,
		'',
	);
}

/**
 * Fit text either by **shrinking / growing the font** inside a fixed
 * rectangle *or* by **expanding / contracting the bounding box** while
 * keeping the font size constant.
 *
 * The behaviour is controlled by `options.kind`:
 *
 * * **`kind: 'font'`** - binary-search the largest font size within
 *   `[minFontSize … maxFontSize]` that satisfies
 *   `width <= boxWidth && height <= boxHeight`.
 * * **`kind: 'box'`**  - keep `fontSize` unchanged, measure the block
 *   once, then:
 *     * `allowGrow`   -> widen the box if the text is wider;
 *     * `allowShrink` -> tighten the box if the text is narrower.
 *
 * Internally a helper **`measure()`** re-runs the wrapping algorithm
 * (`wrapLines()`) for every candidate font or width so that line breaks
 * always match the browser’s own wrapping.
 *
 * @param options                See `FitOptions` (@see FitOptions) union:
 *   • common -> `text`, `fontFamily`, `fontModel`, `fontSize`, `isBold`, `maxWidth`
 *   • font-mode -> `kind: 'font'`, `boxWidth`, `boxHeight`,
 *                 `[min|max]FontSize`
 *   • box-mode  -> `kind: 'box'`, `allowGrow`, `allowShrink`
 *
 * @returns `FitResult` (@see FitResult)
 *   * `width`, `height`      - final block dimensions (px, rounded)
 *   * `usedFontSize`         - actual font size (px, rounded)
 *   * `overflow`             - `true` when even `minFontSize` cannot fit
 */
export function fit(options: FitOptions): FitResult {
	let {
		/**
		 * Might change in `font` mode
		 */
		fontSize: usedFontSize,
		/**
		 * Might change in `box` mode
		 */
		maxWidth,
	} = options;
	const {
		fontFamily,
		fontModel,
		isBold,
		text,
	} = options;
	let width = 0;
	let height = 0;
	let overflow = false;

	if (options.kind === 'font') {
		/**
		 * Mode: fit by font
		 */
		const effectiveMin = (
			(
				options.minFontSize
				&& options.minFontSize > 0
			)
				? options.minFontSize
				: 8
		);
		const effectiveMax = (
			(
				options.maxFontSize
				&& options.maxFontSize > 0
			)
				? options.maxFontSize
				: 300
		);

		let low = effectiveMin;
		let high = effectiveMax;
		/**
		 * Fallback when nothing fits
		 */
		let best = effectiveMin;

		/**
		 * Binary search the largest passing font size
		 */
		while (low <= high) {
			const mid = Math.floor((low + high) / 2);
			const measureResult = measureWrapping({
				fontFamily,
				fontModel,
				isBold,

				measureFn: measure(
					fontFamily,
					mid,
				),
				size: mid,
				text,
				widthLimit: options.boxWidth,
			});

			if (
				measureResult.width <= options.boxWidth
				&& measureResult.height <= options.boxHeight
			) {
				/**
				 * Fits, but we try a larger font size to try get a better fit
				 */
				best = mid;
				low = mid + 1;
			} else {
				/**
				 * Doesn't fit, so we try a smaller font size
				 */
				high = mid - 1;
			}
		}

		usedFontSize = best;
		({
			width,
			height,
		} = measureWrapping({
			fontFamily,
			fontModel,
			isBold,

			measureFn: measure(
				fontFamily,
				usedFontSize,
			),
			size: usedFontSize,
			text,
			widthLimit: options.boxWidth,
		}));
		maxWidth = options.boxWidth;
		overflow = (
			width > options.boxWidth
			|| height > options.boxHeight
		);
	} else {
		/**
		 * Mode: fit by box
		 */
		const measureResult = measureWrapping({
			fontFamily,
			fontModel,
			isBold,

			measureFn: measure(
				fontFamily,
				usedFontSize,
			),
			size: usedFontSize,
			text,
			widthLimit: maxWidth,
		});
		width = measureResult.width;
		height = measureResult.height;

		/**
		 * Grow / shrink the box horizontally if requested
		 */
		if (
			options.allowGrow
			&& width > maxWidth
		) {
			maxWidth = width;
		}
		if (
			options.allowShrink
			&& width < maxWidth
		) {
			maxWidth = width;
		}
	}

	return {
		height: Math.round(height),
		overflow,
		usedFontSize: Math.round(usedFontSize),
		width: Math.round(maxWidth),
	};
}

export function getTextWidth(
	text: string,
	fontFamily: string,
	fontSize: number,
	lineHeight = fontUtils.DEFAULT_LINEHEIGHT,
): number {
	if (!measurer) {
		measurer = document.createElement('div');
		measurer.style.position = 'absolute';
		measurer.style.visibility = 'hidden';
		measurer.style.whiteSpace = 'nowrap';
		measurer.style.left = '-9999px';
		measurer.style.top = '-9999px';
		measurer.style.textTransform = 'none';
		measurer.style.padding = '0';
		measurer.style.margin = '0';
		measurer.style.border = '0 solid';
		document.body.appendChild(measurer);
		measurer.style.fontVariantLigatures = 'no-contextual';
		measurer.style.fontFeatureSettings = '"calt" 0, "rand" 0';
	}

	measurer.style.fontFamily = fontFamily;
	measurer.style.fontSize = `${fontSize}px`;
	measurer.style.lineHeight = String(lineHeight);
	measurer.textContent = text;

	return measurer.getBoundingClientRect().width;
}

export function measure(
	fontFamily: string,
	fontSize: number,
	lruCache = textWidthCache,
	cacheLimit = TEXT_WIDTH_CACHE_LIMIT,
): (textToMeasure: string) => number {
	const key: WidthScaleKey = `${fontFamily}|${fontSize}`;
	/**
	 * Ensure we have a per-font LRU map ready to cache individual string widths.
	 */
	let lru = lruCache.get(key);

	if (!lru) {
		lru = new Map<string, number>();
		lruCache.set(
			key,
			lru,
		);
	}

	return (textToMeasure) => {
		const cached = lru.get(textToMeasure);

		/**
		 * Fast path - return cached width and refresh recency.
		 */
		if (cached) {
			/**
			 * Refresh LRU recency by re-inserting the same entry.
			 */
			lru.delete(textToMeasure);
			lru.set(
				textToMeasure,
				cached,
			);

			return cached;
		}

		const measured = getTextWidth(
			textToMeasure,
			fontFamily,
			fontSize,
		);

		/**
		 * Maintain LRU capacity:
		 * - If at capacity, evict the least-recently-used (Map's first key).
		 * - Insert the new measurement as most-recent.
		 */
		if (lru.size >= cacheLimit) {
			const oldestKey = lru.keys().next().value as string;
			lru.delete(oldestKey);
		}

		lru.set(
			textToMeasure,
			measured,
		);

		return measured;
	};
}

/**
 * **Browser-faithful line-wrapper**
 *
 * Recreates the way a `<textarea>` soft-wraps text so that the SVG we generate
 * uses *exactly* the same breakpoints. The algorithm works entirely in
 * **canvas space** (`measureText`) so it respects the real glyph metrics of the
 * active font.
 *
 * Features:
 * 1. **Hard breaks** — every `\r?\n` forces a new visual line.
 * 2. **Whitespace wrapping** - splits on runs of ASCII / Unicode spaces.
 * 3. **Soft-hyphen handling** - a {@link SOFT_HYPHEN} token *commits* the current buffer
 *    (plus a visible "-") and starts a new line.
 * 4. **Over-wide word splitting** - delegates to `breakLongWord()` so huge words
 *    break at grapheme boundaries without exceeding `maxWidth`.
 * 5. **Trailing spaces** - faithfully replays any blanks at the end of a
 *    paragraph, even if they overflow onto a new line.
 *
 * @param text         Raw textarea value.
 * @param canvasContext 2-D context whose font = textarea font.
 * @param maxWidth     Maximum line width in CSS pixels.
 *
 * @returns `WrapLinesResult` (@see WrapLinesResult)
 *          * `lines`          - array of *visual* lines (empty string means a
 *                               blank line).
 *          * `maxLineWidth`   - width (px) of the widest line, useful for box
 *                               mode in the fit helper.
 */
export function wrapLines(
	text: string,
	maxWidth: number,
	measureFn: (text: string) => number,
): WrapLinesResult {
	const lines: string[] = [];
	let widest = 0;
	const paragraphs = text.split(/\r?\n/);

	for (const paragraph of paragraphs) {
		/**
		 * Hard-return -> preserve blank visual line
		 */
		if (paragraph === '') {
			lines.push('');
			continue;
		}

		/**
		 * Split paragraph into "words" where a soft-hyphen or any
		 * whitespace boundary may become a break opportunity.
		 */
		const words = paragraph.split(new RegExp(
			`(${SOFT_HYPHEN}|-|\\u2010|\\u2013|\\u2014)|\\s+`,
		));
		/**
		 * Buffer that builds the candidate visual line
		 */
		let current = '';
		/**
		 * Used to store either '' or ' ' depending on word position
		 */
		let space = '';

		for (const word of words) {
			/**
			 * Ignore empty tokens (multiple spaces or the split delimiters)
			 */
			if (
				word === undefined
				|| word === ''
			) {
				space = ' ';
				continue;
			}

			/**
			 * Soft-hyphen token - commit buffer + visible hyphen
			 */
			if (word === SOFT_HYPHEN) {
				if (current !== '') {
					const hyphenated = `${current}-`;
					lines.push(hyphenated);
					widest = Math.max(
						widest,
						measureFn(hyphenated),
					);
					/**
					 * Start building the next visual line
					 */
					current = '';
				}

				/**
				 * No leading space after a hyphen break
				 */
				space = '';
				continue;
			}

			/**
			 * Hard hyphen / dash token — *optional* break opportunity.
			 * We append it to the current buffer if it fits; if it doesn't,
			 * we break the line before it so the hyphen/dash starts the next line.
			 * On the next iteration, adding the following word may overflow,
			 * in which case the line naturally ends with this hyphen/dash.
			 */
			if (
				word === '-'
				|| word === '\u2010'
				|| word === '\u2013'
				|| word === '\u2014'
			) {
				const hyphenTentative = current + word;

				if (measureFn(hyphenTentative) <= maxWidth) {
					current = hyphenTentative;
				} else {
					if (current) {
						lines.push(current);
						widest = Math.max(
							widest,
							measureFn(current),
						);
					}

					current = word;
				}

				/**
				 * Dashes bind tight — no whitespace around them by default.
				 */
				space = '';
				continue;
			}

			/**
			 * Normal word token - test if it fits on this line
			 */
			const tentative = current + space + word;

			if (measureFn(tentative) <= maxWidth) {
				current = tentative;
				space = ' ';
				/**
				 * Still fits -> keep buffering
				 */
				continue;
			}

			/**
			 * Current buffer is non-empty and now overflows -> push it
			 */
			if (current) {
				lines.push(current);
				widest = Math.max(
					widest,
					measureFn(current),
				);
				current = '';
			}

			/**
			 * Word itself is wider than the box -> split with {@link breakLongWord}
			 */
			const parts = (
				measureFn(word) > maxWidth
					? breakLongWord(
						word,
						maxWidth,
						measureFn,
					)
					: [word]
			);

			for (const part of parts) {
				if (measureFn(part) <= maxWidth) {
					if (!current) {
						current = part;
					} else {
						lines.push(current);
						widest = Math.max(
							widest,
							measureFn(current),
						);
						current = part;
					}
				}
			}

			space = ' ';
		}

		/**
		 * Push whatever is left in the buffer as the final line of the paragraph
		 */
		lines.push(current);
		widest = Math.max(
			widest,
			measureFn(current),
		);

		/**
		 * Preserve trailing spaces
		 */
		const trailing = paragraph.match(/(\s+)$/);

		/**
		 * Handle trailing spaces by calculating how many spaces can fit in the remaining width
		 * of the current line and adding them efficiently.
		 */
		if (trailing) {
			/**
			 * Extract the trailing spaces from the match result.
			 */
			const tail = trailing[1];
			/**
			 * Measure the width of a single space character using the canvas context.
			 */
			const spaceWidth = measureFn(' ');
			/**
			 * Calculate the remaining width available in the current line by subtracting the
			 * width of the current line's text from the maximum allowed width.
			 */
			const remainingWidth = maxWidth - measureFn(lines[lines.length - 1]);
			/**
			 * Determine how many spaces can fit into the remaining width by dividing
			 * the remaining width by the width of a single space and taking the floor.
			 */
			const spacesToAdd = Math.floor(remainingWidth / spaceWidth);
			/**
			 * Append the maximum number of spaces that can fit in the current line, limited
			 * by either the calculated `spacesToAdd` or the actual number of trailing spaces.
			 */
			lines[lines.length - 1] += ' '.repeat(Math.min(
				spacesToAdd,
				tail.length,
			));

			/**
			 * Check if there are more trailing spaces than can fit in the current line.
			 */
			if (spacesToAdd < tail.length) {
				/**
				 * Add the remaining spaces as a new line.
				 */
				lines.push(tail.slice(spacesToAdd));
			}
		}
	}

	return {
		lines,
		maxLineWidth: widest,
	};
}
